import { DomainError } from './errors.ts';

/**
 * The fewest completed participants whose answers are worth reading: no audience may ask for fewer,
 * at any write, approval, purchase or provisioning. One product-wide value, read by the SPA, the
 * server and the Brief alike, so a study's label means the same everywhere.
 *
 * A domain rule rather than a schema minimum, deliberately: raising it must not make a stored
 * audience — or a launched study's Brief — unreadable, and a refusal that names the floor is
 * something a researcher, or the agent, can act on where a bare schema failure is not.
 */
export const QUALITY_FLOOR = 20;

/**
 * The number of completed participants at and above which a study's read is firmer than
 * directional. Assigned from what actually completed, never from N, so nothing in drafting or
 * approval decides by it.
 */
export const CONFIDENCE_THRESHOLD = 50;

/** The refusal's words, which the Audience step shows as they are. */
export const QUALITY_FLOOR_REFUSAL = `A study needs at least ${String(QUALITY_FLOOR)} people for its answers to be worth reading, so the count can't go below ${String(QUALITY_FLOOR)}.`;

/** Refuses an audience asking for fewer people than the Quality floor, naming the floor. */
export const assertMeetsQualityFloor = (content: { target_completes: number }): void => {
  if (content.target_completes < QUALITY_FLOOR) {
    throw new DomainError('audience_below_quality_floor', QUALITY_FLOOR_REFUSAL, {
      floor: QUALITY_FLOOR,
      requested: content.target_completes,
    });
  }
};

/** How firm a study's read is, from what actually completed: the two tiers a Brief can carry. */
export const CONFIDENCE_TIERS = ['directional', 'solid'] as const;
export type ConfidenceTier = (typeof CONFIDENCE_TIERS)[number];

/** The Confidence line a Brief shows: the tier, its label and the sentence under it. */
export interface Confidence {
  tier: ConfidenceTier;
  label: string;
  explanation: string;
}

/** What each tier says, given the people it is based on. */
const CONFIDENCE_WORDING: Record<ConfidenceTier, { label: string; explanation: string }> = {
  directional: {
    label: 'Directional read',
    explanation: 'Enough to see which way opinion leans, not to size it precisely.',
  },
  solid: {
    label: 'Solid read',
    explanation: 'Enough to rely on the pattern and its rough size.',
  },
};

/**
 * The Confidence line for a tier and the people it is based on, as stored. A published Brief is
 * rendered through this rather than `confidenceFor`, so its label is the tier it was published with
 * whatever the thresholds say later.
 */
export const confidenceOf = (tier: ConfidenceTier, completes: number): Confidence => ({
  tier,
  label: CONFIDENCE_WORDING[tier].label,
  explanation: `Based on ${String(completes)} people. ${CONFIDENCE_WORDING[tier].explanation}`,
});

/**
 * The Confidence a study's read carries, from its actual completes: a directional read from the
 * Quality floor up to the confidence threshold, a solid read at and above it, and none below the
 * floor, where a study gets no Brief at all. The wording lives in `confidenceOf`, so a Brief stores
 * the tier and the count and every reader renders the same words from them.
 */
export const confidenceFor = (completes: number): Confidence | null => {
  if (completes < QUALITY_FLOOR) {
    return null;
  }

  return confidenceOf(completes < CONFIDENCE_THRESHOLD ? 'directional' : 'solid', completes);
};
